import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { UtensilsCrossed, X, Eye, EyeOff } from "lucide-react";
import {
  useAdminAuth,
  AdminLogin,
  AdminLoading,
  ConfirmDelete,
} from "./AdminAuth";
import { DeleteBtn } from "./AdminListPage";
import AdminBar from "./AdminBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-sm border border-[#102C1F]/25 bg-[#F4F1EB] px-4 py-2.5 text-sm text-[#102C1F] outline-none transition-colors duration-300 placeholder:text-[#102C1F]/40 focus:border-[#102C1F]";

const tfield = field.replace("px-4 py-2.5", "px-3 py-2");

export default function AdminCartePage() {
  const [user, setUser] = useAdminAuth();
  const [menuDishes, setMenuDishes] = useState([]);
  const [toDelete, setToDelete] = useState(null);
  const [uploading, setUploading] = useState({});
  const [catFilter, setCatFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [extraCats, setExtraCats] = useState([]);
  const [showCat, setShowCat] = useState(false);
  const [catName, setCatName] = useState("");
  const [newDish, setNewDish] = useState({
    name: "",
    desc: "",
    price: "",
    category: "",
    img: "",
  });

  const uploadDishImage = async (i, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading((p) => ({ ...p, [i]: 0 }));
    try {
      const { data } = await axios.post(`${API}/upload-image`, fd, {
        withCredentials: true,
        onUploadProgress: (e) =>
          setUploading((p) => ({
            ...p,
            [i]: Math.round((e.loaded * 100) / (e.total || 1)),
          })),
      });
      updateMenuDish(i, "img", data.url);
      toast.success("Photo téléchargée ! Pensez à Enregistrer.");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Impossible d'envoyer l'image."
      );
    } finally {
      setUploading((p) => {
        const n = { ...p };
        delete n[i];
        return n;
      });
    }
  };

  const load = useCallback(async () => {
    const [d, c] = await Promise.all([
      axios.get(`${API}/dishes?all=true`, { withCredentials: true }),
      axios.get(`${API}/menu-categories`),
    ]);
    setMenuDishes(d.data);
    setExtraCats(c.data.items || []);
  }, []);

  useEffect(() => {
    if (!user) return;
    load().catch(() => {});
  }, [user, load]);

  const uploadNewImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading((p) => ({ ...p, new: 0 }));
    try {
      const { data } = await axios.post(`${API}/upload-image`, fd, {
        withCredentials: true,
        onUploadProgress: (e) =>
          setUploading((p) => ({
            ...p,
            new: Math.round((e.loaded * 100) / (e.total || 1)),
          })),
      });
      setNewDish((p) => ({ ...p, img: data.url }));
      toast.success("Photo téléchargée !");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Impossible d'envoyer l'image."
      );
    } finally {
      setUploading((p) => {
        const n = { ...p };
        delete n.new;
        return n;
      });
    }
  };

  const saveNewDish = async () => {
    if (!newDish.name.trim()) {
      toast.error("Nom du plat obligatoire.");
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/dishes`,
        {
          name: newDish.name,
          desc: newDish.desc,
          price: Number(newDish.price) || 0,
          img: newDish.img,
          category: newDish.category || "",
        },
        { withCredentials: true }
      );
      setMenuDishes((prev) => [...prev, data]);
      setNewDish({ name: "", desc: "", price: "", category: "", img: "" });
      setShowAdd(false);
      toast.success(`« ${data.name} » ajouté !`);
    } catch {
      toast.error("Impossible d'enregistrer. Reconnectez-vous.");
    }
  };

  const updateMenuDish = (i, key, val) =>
    setMenuDishes((prev) =>
      prev.map((x, j) => (j === i ? { ...x, [key]: val } : x))
    );

  const removeMenuDish = (d, i) => {
    if (!d.id) {
      setMenuDishes((prev) => prev.filter((_, j) => j !== i));
      return;
    }
    setToDelete({ tipo: "dishes", id: d.id, name: d.name });
  };

  const saveMenuDish = async (d, i) => {
    if (!d.name.trim()) {
      toast.error("Nom du plat obligatoire.");
      return;
    }
    try {
      if (!d.id) {
        const { data } = await axios.post(
          `${API}/dishes`,
          { name: d.name, desc: d.desc, price: Number(d.price), img: d.img, category: d.category || "", min_qty: Number(d.min_qty) || 1 },
          { withCredentials: true }
        );
        setMenuDishes((prev) => prev.map((x, j) => (j === i ? data : x)));
        toast.success(`« ${data.name} » ajouté !`);
      } else {
        await axios.put(
          `${API}/dishes/${d.id}`,
          { name: d.name, desc: d.desc, price: Number(d.price), img: d.img, category: d.category || "", min_qty: Number(d.min_qty) || 1, visible: d.visible !== false },
          { withCredentials: true }
        );
        toast.success(`« ${d.name} » mis à jour !`);
      }
    } catch {
      toast.error("Impossible d'enregistrer. Reconnectez-vous.");
    }
  };

  const toggleDishVisible = async (d) => {
    if (!d.id) return;
    const next = d.visible === false;
    try {
      await axios.put(
        `${API}/dishes/${d.id}`,
        { name: d.name, desc: d.desc, price: Number(d.price), img: d.img, category: d.category || "", min_qty: Number(d.min_qty) || 1, visible: next },
        { withCredentials: true }
      );
      setMenuDishes((prev) =>
        prev.map((x) => (x.id === d.id ? { ...x, visible: next } : x))
      );
      toast.success(
        next ? `« ${d.name} » visible sur le site.` : `« ${d.name} » masqué du site.`
      );
    } catch {
      toast.error("Impossible d'enregistrer. Reconnectez-vous.");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/dishes/${toDelete.id}`, {
        withCredentials: true,
      });
      setMenuDishes((prev) => prev.filter((d) => d.id !== toDelete.id));
      toast.success("Supprimé.");
    } catch {
      toast.error("Impossible de supprimer.");
    }
    setToDelete(null);
  };

  const saveCategory = async () => {
    const name = catName.trim();
    if (!name) {
      toast.error("Nom de la catégorie obligatoire.");
      return;
    }
    try {
      const { data } = await axios.put(
        `${API}/menu-categories`,
        { items: [...extraCats, name] },
        { withCredentials: true }
      );
      setExtraCats(data.items);
      setCatName("");
      setShowCat(false);
      toast.success(`Catégorie « ${name} » créée !`);
    } catch {
      toast.error("Impossible d'enregistrer. Reconnectez-vous.");
    }
  };

  if (user === null) return <AdminLoading />;

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F1EB] px-5">
        <AdminLogin onSuccess={setUser} title="Carte Emporter" />
      </div>
    );
  }

  const categories = [
    ...new Set([...extraCats, ...menuDishes.map((d) => d.category || "")]),
  ];
  const filtered =
    catFilter === ""
      ? menuDishes
      : menuDishes.filter((d) => (d.category || "") === catFilter);

  return (
    <div
      data-testid="admin-carte-page"
      className="min-h-screen bg-[#F4F1EB] text-[#102C1F]"
    >
      <AdminBar />
      <header className="bg-[#102C1F] text-[#F4F1EB]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <span className="flex items-center gap-3 font-display text-xl italic md:text-2xl">
            <UtensilsCrossed size={20} /> Carte Emporter
          </span>
          <Link
            data-testid="carte-dashboard-link"
            to="/admin"
            className="rounded-full border border-[#F4F1EB]/30 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F]"
          >
            Gestion
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-12 md:px-10">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <select
            data-testid="carte-category-filter"
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
            className={`${field} w-auto`}
          >
            <option value="">Toutes les catégories</option>
            {categories
              .filter(Boolean)
              .map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            {categories.includes("") && (
              <option value="__none__">Sans catégorie</option>
            )}
          </select>
          <span
            data-testid="carte-count"
            className="text-xs uppercase tracking-[0.2em] text-[#102C1F]/50"
          >
            {filtered.length} {filtered.length === 1 ? "plat" : "plats"}
          </span>
          <button
            data-testid="menu-category-add-btn"
            onClick={() => setShowCat(true)}
            className="ml-auto rounded-full border border-dashed border-[#102C1F]/40 px-6 py-3 text-xs uppercase tracking-[0.25em] text-[#102C1F]/70 transition-colors duration-300 hover:border-[#102C1F] hover:text-[#102C1F]"
          >
            + Catégorie
          </button>
          <button
            data-testid="menu-dish-add-btn"
            onClick={() => setShowAdd(true)}
            className="rounded-full border border-dashed border-[#102C1F]/40 px-6 py-3 text-xs uppercase tracking-[0.25em] text-[#102C1F]/70 transition-colors duration-300 hover:border-[#102C1F] hover:text-[#102C1F]"
          >
            + Ajouter un plat
          </button>
        </div>

        <div className="overflow-x-auto rounded-sm border border-[#102C1F]/15">
          <table className="w-full min-w-[980px] text-sm">
            <thead>
              <tr className="bg-[#102C1F] text-left text-xs uppercase tracking-[0.15em] text-[#F4F1EB]">
                <th className="px-4 py-3">Photo</th>
                <th className="px-4 py-3">Nom du plat</th>
                <th className="px-4 py-3">Prix (€)</th>
                <th className="px-4 py-3">Catégorie</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const i = menuDishes.indexOf(d);
                return (
                  <tr
                    key={d.id || `new-${i}`}
                    data-testid={`menu-dish-card-${i}`}
                    className={`border-t border-[#102C1F]/10 bg-[#EDE8DE] align-top transition-opacity duration-300 ${d.visible === false ? "opacity-50" : ""}`}
                  >
                    <td className="w-52 px-4 py-3">
                      {d.img && (
                        <img
                          src={d.img}
                          alt={d.name}
                          className="mb-2 h-14 w-20 rounded-sm object-cover"
                        />
                      )}
                      <label
                        data-testid={`menu-dish-upload-${i}`}
                        className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-[#102C1F]/30 px-3 py-1.5 text-[9px] uppercase tracking-[0.15em] text-[#102C1F]/70 transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
                      >
                        <input
                          data-testid={`menu-dish-file-${i}`}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            uploadDishImage(i, e.target.files[0]);
                            e.target.value = "";
                          }}
                        />
                        {uploading[i] != null
                          ? `Envoi ${uploading[i]}%`
                          : "Photo"}
                      </label>
                      <input
                        data-testid={`menu-dish-img-${i}`}
                        value={d.img}
                        placeholder="ou URL https://..."
                        onChange={(e) => updateMenuDish(i, "img", e.target.value)}
                        className={`${tfield} mt-2 text-xs`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        data-testid={`menu-dish-name-${i}`}
                        value={d.name}
                        onChange={(e) => updateMenuDish(i, "name", e.target.value)}
                        className={tfield}
                      />
                    </td>
                    <td className="w-28 px-4 py-3">
                      <input
                        data-testid={`menu-dish-price-${i}`}
                        type="number"
                        step="0.01"
                        min="0"
                        value={d.price}
                        onChange={(e) => updateMenuDish(i, "price", e.target.value)}
                        className={tfield}
                      />
                      <span className="mt-2 block text-[10px] uppercase tracking-[0.15em] text-[#102C1F]/50">
                        Qté min.
                      </span>
                      <input
                        data-testid={`menu-dish-min-qty-${i}`}
                        type="number"
                        min="1"
                        step="1"
                        value={d.min_qty || 1}
                        onChange={(e) => updateMenuDish(i, "min_qty", e.target.value)}
                        className={`${tfield} mt-1`}
                      />
                    </td>
                    <td className="w-48 px-4 py-3">
                      <select
                        data-testid={`menu-dish-category-${i}`}
                        value={d.category || ""}
                        onChange={(e) =>
                          updateMenuDish(i, "category", e.target.value)
                        }
                        className={`${tfield} ${!d.category ? "text-[#102C1F]/40" : ""}`}
                      >
                        <option value="">Sans catégorie</option>
                        {categories
                          .filter(Boolean)
                          .map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <textarea
                        data-testid={`menu-dish-desc-${i}`}
                        rows={3}
                        value={d.desc}
                        onChange={(e) => updateMenuDish(i, "desc", e.target.value)}
                        className={`${tfield} min-w-72 resize-y`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          data-testid={`menu-dish-visible-${i}`}
                          onClick={() => toggleDishVisible(d)}
                          title={
                            d.visible === false
                              ? "Afficher sur le site"
                              : "Masquer du site"
                          }
                          aria-label={
                            d.visible === false
                              ? "Afficher sur le site"
                              : "Masquer du site"
                          }
                          className={`rounded-full border p-2 transition-colors duration-300 ${
                            d.visible === false
                              ? "border-[#102C1F]/20 text-[#102C1F]/40 hover:border-[#102C1F] hover:text-[#102C1F]"
                              : "border-[#102C1F]/40 text-[#102C1F] hover:bg-[#102C1F] hover:text-[#F4F1EB]"
                          }`}
                        >
                          {d.visible === false ? (
                            <EyeOff size={15} />
                          ) : (
                            <Eye size={15} />
                          )}
                        </button>
                        <button
                          data-testid={`menu-dish-save-${i}`}
                          onClick={() => saveMenuDish(d, i)}
                          className="rounded-full bg-[#102C1F] px-5 py-2 text-xs uppercase tracking-[0.2em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
                        >
                          {d.id ? "Enregistrer" : "Ajouter"}
                        </button>
                        <DeleteBtn
                          testid={`menu-dish-delete-${i}`}
                          onClick={() => removeMenuDish(d, i)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>

      {showCat && (
        <div
          data-testid="add-category-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-5"
        >
          <div className="relative w-full max-w-sm rounded-sm border border-[#102C1F]/20 bg-[#F4F1EB] p-8">
            <button
              data-testid="add-category-close"
              onClick={() => {
                setShowCat(false);
                setCatName("");
              }}
              aria-label="Fermer"
              className="absolute right-4 top-4 text-[#102C1F]/50 transition-colors duration-300 hover:text-[#102C1F]"
            >
              <X size={20} />
            </button>
            <h3 className="mb-6 font-display text-2xl italic tracking-tight">
              Nouvelle catégorie
            </h3>
            <div className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                  Nom de la catégorie *
                </span>
                <input
                  data-testid="add-category-name"
                  value={catName}
                  placeholder="Ex : Entrées"
                  onChange={(e) => setCatName(e.target.value)}
                  className={field}
                />
              </label>
              <button
                data-testid="add-category-submit"
                onClick={saveCategory}
                className="w-full rounded-full bg-[#102C1F] px-5 py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div
          data-testid="add-dish-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-5"
        >
          <div className="relative w-full max-w-md rounded-sm border border-[#102C1F]/20 bg-[#F4F1EB] p-8">
            <button
              data-testid="add-dish-close"
              onClick={() => {
                setShowAdd(false);
                setNewDish({
                  name: "",
                  desc: "",
                  price: "",
                  category: "",
                  img: "",
                });
              }}
              aria-label="Fermer"
              className="absolute right-4 top-4 text-[#102C1F]/50 transition-colors duration-300 hover:text-[#102C1F]"
            >
              <X size={20} />
            </button>
            <h3 className="mb-6 font-display text-2xl italic tracking-tight">
              Nouveau plat
            </h3>
            <div className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                  Nom du plat *
                </span>
                <input
                  data-testid="add-dish-name"
                  value={newDish.name}
                  onChange={(e) =>
                    setNewDish((p) => ({ ...p, name: e.target.value }))
                  }
                  className={field}
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                    Prix (€)
                  </span>
                  <input
                    data-testid="add-dish-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={newDish.price}
                    onChange={(e) =>
                      setNewDish((p) => ({ ...p, price: e.target.value }))
                    }
                    className={field}
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                    Catégorie
                  </span>
                  <select
                    data-testid="add-dish-category"
                    value={newDish.category}
                    onChange={(e) =>
                      setNewDish((p) => ({ ...p, category: e.target.value }))
                    }
                    className={`${field} ${!newDish.category ? "text-[#102C1F]/40" : ""}`}
                  >
                    <option value="">Sans catégorie</option>
                    {categories
                      .filter(Boolean)
                      .map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                  Description
                </span>
                <input
                  data-testid="add-dish-desc"
                  value={newDish.desc}
                  onChange={(e) =>
                    setNewDish((p) => ({ ...p, desc: e.target.value }))
                  }
                  className={field}
                />
              </label>
              <div>
                <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                  Photo
                </span>
                <div className="flex items-center gap-3">
                  {newDish.img && (
                    <img
                      src={newDish.img}
                      alt=""
                      className="h-14 w-20 rounded-sm object-cover"
                    />
                  )}
                  <label
                    data-testid="add-dish-upload"
                    className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-[#102C1F]/30 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#102C1F]/70 transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
                  >
                    <input
                      data-testid="add-dish-file"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        uploadNewImage(e.target.files[0]);
                        e.target.value = "";
                      }}
                    />
                    {uploading.new != null
                      ? `Envoi ${uploading.new}%`
                      : "Télécharger"}
                  </label>
                </div>
                <input
                  data-testid="add-dish-img"
                  value={newDish.img}
                  placeholder="ou URL https://..."
                  onChange={(e) =>
                    setNewDish((p) => ({ ...p, img: e.target.value }))
                  }
                  className={`${field} mt-2 text-xs`}
                />
              </div>
              <button
                data-testid="add-dish-submit"
                onClick={saveNewDish}
                className="w-full rounded-full bg-[#102C1F] px-5 py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDelete
        item={toDelete}
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
